import { useEffect } from "react";
import { Text } from "@fluentui/react/lib/Text";
import useRouter from "hooks/useRouter";
import { IAgreementObjectId } from "models/agreements";
import { routeDefinitions } from "router";
import { useGetPolicyDetailsQuery } from "services/policy";
import { environmentConfig } from "config/environmentConfig";
import { IPolicyInfo } from "./types";
import PolicyEditHeader from "./PolicyEditHeader";
import PolicyEditPivot from "./PolicyEditPivot";
import { PolicyEditStructureNodeManagerProvider } from "./structure/PolicyEditStructureNodeManager";

function PolicyEdit({ policyId }: IAgreementObjectId) {
  const { updateRoute } = useRouter();
  
  // Fetch detailed policy data including criteria from DevOps
  const { data: policyDetails, isLoading: isLoadingDetails } = useGetPolicyDetailsQuery(
    {
      id: policyId || "",
      cloud: environmentConfig.cloud,
      environment: environmentConfig.environment,
      policyType: "Implicit Acceptance", // Default type, will be overridden by actual data
    },
    {
      skip: !policyId,
    }
  );
  
  const isLoading = isLoadingDetails;
  
  // Create policyInfo from detailed policy data
  const policyInfo: IPolicyInfo = {
    id: policyId || "",
    name: policyDetails?.name || "Loading...",
    description: policyDetails?.description || "",
    type: policyDetails?.type || "Implicit Acceptance",
    status: policyDetails?.status || "Draft",
    template: policyDetails?.template || undefined,
    templateId: policyDetails?.templateId || undefined,
    isTestPolicy: policyDetails?.isTestPolicy || false,
    agreementEffectiveDate: undefined,
    criteria: policyDetails?.criteria || [],
    hasData: !!policyDetails,
    isLoading
  };

  useEffect(() => {
    if (policyId) {
      updateRoute(
        routeDefinitions.PolicyEdit.getRouteInfo({
          policyId,
        })
      );
    }
  }, [policyId, updateRoute]);

  const isPolicyEditable = policyInfo.hasData && !policyInfo.isLoading;

  // Main rendering with error boundary
  try {
    return (
      <PolicyEditStructureNodeManagerProvider policyId={policyId} policyInfo={policyInfo}>
        <PolicyEditHeader policyInfo={policyInfo} />
        {isPolicyEditable && <PolicyEditPivot policyInfo={policyInfo} />}
      </PolicyEditStructureNodeManagerProvider>
    );
  } catch (error) {
    return (
      <div style={{ padding: "20px" }}>
        <Text variant="xLarge" style={{ marginBottom: "16px" }}>
          Policy Edit - {policyInfo.name}
        </Text>
        <Text variant="medium" style={{ marginBottom: "12px" }}>
          Policy ID: {policyId}
        </Text>
        <Text variant="medium" style={{ marginBottom: "12px" }}>
          Description: {policyInfo.description}
        </Text>
        <Text variant="medium" style={{ marginBottom: "12px" }}>
          Type: {policyInfo.type}
        </Text>
        <Text variant="medium" style={{ marginBottom: "12px" }}>
          Status: {policyInfo.status}
        </Text>
        <Text variant="small" style={{ color: "#666" }}>
          Policy editing interface is loading... Please refresh the page if this persists.
        </Text>
      </div>
    );
  }
}

export default PolicyEdit;